/**
 * Main chat layout: session sidebar, empty-state suggestions, or active thread.
 */
import React, { useEffect, useState } from 'react';
import { keyframes } from '@emotion/react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChatSidebar from '../ChatSidebar';
import ChatInput from '../ChatInput';
import ChatMessageList from './ChatMessageList';

const INTRO_TITLE = 'Meet Guardian';

// Milliseconds between each typed letter of the intro heading. Higher = slower.
const TYPING_SPEED_MS = 100;

const suggestionFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SUGGESTIONS = [
  // Shown on the empty "Meet Guardian" screen; clicking fills the composer.
  { label: 'What can you help me with?', message: 'What can you help me with?' },
  {
    label: 'Check risk of ignition',
    message: 'What is the risk of ignition at 34.05 -118.25?',
  },
  {
    label: 'Generate situation report',
    message: 'Generate a situation report for the Santa Monica Mountains area',
  },
  {
    label: 'Tactics for similar fires',
    message: 'What tactics have worked for brush fires in similar conditions?',
  },
];

/**
 * Sidebar + main chat column. Shows empty-state suggestions or the active thread.
 *
 * @param {object} props
 * @param {Array} props.sessions - Saved chat sessions for the sidebar.
 * @param {string} props.sessionId - Active session id.
 * @param {Array} props.messages - Messages in the active session.
 * @param {boolean} props.sidebarCollapsed
 * @param {React.RefObject} props.messagesEndRef
 * @param {object} props.chatInputProps - Spread into ChatInput.
 * @param {() => void} props.onToggleSidebar
 * @param {() => void} props.onNewChat
 * @param {(id: string) => void} props.onSelectSession
 * @param {(id: string) => void} props.onDeleteSession
 * @param {(message: string) => void} props.onSuggestionClick - Empty-state chip handler.
 * @param {(confirmationId: string) => void} props.onConfirmReport - Approves a
 *   pending confirmation card.
 * @param {(messageId: string, value: 1|-1) => void} [props.onRate] - Rates an answer.
 * @param {(message: object) => void} [props.onShare] - Shares an answer to the department board.
 */
export default function ChatWorkspace({
  sessions,
  sessionId,
  messages,
  sidebarCollapsed,
  messagesEndRef,
  chatInputProps,
  onToggleSidebar,
  onNewChat,
  onSelectSession,
  onDeleteSession,
  onSuggestionClick,
  onConfirmReport,
  onRate,
  onShare,
  // Server-side history (audit P-12). When `historySessions` is an array the
  // agent supports history and the sidebar lists the user's own conversations;
  // selecting one sets `viewedThread` and the thread renders read-only. When it
  // is null the agent is older and the local history above is used unchanged.
  historySessions = null,
  viewedThread = null,
  onSelectHistory,
  onExitHistory,
}) {
  const theme = useTheme();
  const themeMode = theme.palette.mode;
  const serverMode = Array.isArray(historySessions);
  const viewing = Boolean(serverMode && viewedThread);
  const isEmptyChat = messages.length === 0;

  const sidebarSessions = serverMode ? historySessions : sessions;
  const sidebarActiveId = serverMode ? (viewedThread?.id || null) : sessionId;
  const handleSidebarSelect = serverMode ? onSelectHistory : onSelectSession;
  const handleSidebarNew = serverMode
    ? () => { if (onExitHistory) onExitHistory(); onNewChat(); }
    : onNewChat;
  const handleSidebarDelete = serverMode ? (() => {}) : onDeleteSession;
  const [typedTitle, setTypedTitle] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Type the empty-state heading, then reveal prompt suggestions in sequence.
  useEffect(() => {
    if (!isEmptyChat) {
      setTypedTitle('');
      setShowSuggestions(false);
      return undefined;
    }

    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      setTypedTitle(INTRO_TITLE);
      setShowSuggestions(true);
      return undefined;
    }

    setTypedTitle('');
    setShowSuggestions(false);
    let characterIndex = 0;
    let revealTimer;

    const typingTimer = window.setInterval(() => {
      characterIndex += 1;
      setTypedTitle(INTRO_TITLE.slice(0, characterIndex));

      if (characterIndex === INTRO_TITLE.length) {
        window.clearInterval(typingTimer);
        revealTimer = window.setTimeout(() => setShowSuggestions(true), 300);
      }
    }, TYPING_SPEED_MS);

    return () => {
      window.clearInterval(typingTimer);
      window.clearTimeout(revealTimer);
    };
  }, [isEmptyChat]);

  const suggestionChipSx = {
    bgcolor: themeMode === 'dark' ? '#1a3052' : theme.palette.secondary.main,
    color: themeMode === 'dark' ? '#93c5fd' : theme.palette.secondary.contrastText,
    border: '1px solid',
    borderColor: 'divider',
    cursor: 'pointer',
    '&:hover': {
      bgcolor: themeMode === 'dark' ? '#243b5c' : 'action.hover',
    },
  };

  return (
    <>
      <ChatSidebar
        sessions={sidebarSessions}
        activeSessionId={sidebarActiveId}
        collapsed={sidebarCollapsed}
        onToggleCollapse={onToggleSidebar}
        onNewChat={handleSidebarNew}
        onSelectSession={handleSidebarSelect}
        onDeleteSession={handleSidebarDelete}
        hideDelete={serverMode}
      />

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          minHeight: 0,
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {viewing ? (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                px: 3,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                flexShrink: 0,
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Viewing a saved conversation (read-only).
              </Typography>
              <Button size="small" variant="outlined" onClick={onExitHistory} sx={{ textTransform: 'none' }}>
                Back to live chat
              </Button>
            </Box>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                bgcolor: 'background.default',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <Box
                role="log"
                aria-label="Saved conversation"
                sx={{
                  width: '100%',
                  maxWidth: 768,
                  mx: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {viewedThread.messages.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    This conversation has no messages.
                  </Typography>
                ) : (
                  <ChatMessageList
                    messages={viewedThread.messages}
                    isLoading={false}
                    messagesEndRef={messagesEndRef}
                  />
                )}
              </Box>
            </Box>
          </>
        ) : isEmptyChat ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              width: '100%',
              display: 'grid',
              placeItems: 'center',
              px: 3,
              boxSizing: 'border-box',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: 720,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                my: 'auto',
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  color: 'text.primary',
                  mb: 3,
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                {typedTitle}
                {typedTitle.length < INTRO_TITLE.length && (
                  <Box component="span" sx={{ color: 'primary.main', ml: 0.25 }}>
                    |
                  </Box>
                )}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  justifyContent: 'center',
                  alignItems: 'center',
                  mb: 3,
                  width: '100%',
                  minHeight: 32,
                }}
              >
                {showSuggestions && SUGGESTIONS.map((item, index) => (
                    <Chip
                      key={item.label}
                      label={item.label}
                      onClick={() => onSuggestionClick(item.message)}
                      sx={{
                        ...suggestionChipSx,
                        opacity: 0,
                        animation: `${suggestionFadeIn} 420ms ease-out forwards`,
                        animationDelay: `${index * 160}ms`,
                        '@media (prefers-reduced-motion: reduce)': {
                          opacity: 1,
                          animation: 'none',
                        },
                      }}
                    />
                  ))}
              </Box>
              <Box sx={{ width: '100%' }}>
                <ChatInput {...chatInputProps} centered />
              </Box>
            </Box>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                bgcolor: 'background.default',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <Box
                // New answers are announced to screen readers (audit P-17).
                role="log"
                aria-live="polite"
                aria-relevant="additions"
                aria-label="Conversation"
                sx={{
                  width: '100%',
                  maxWidth: 768,
                  mx: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <ChatMessageList
                  messages={messages}
                  isLoading={chatInputProps.isLoading}
                  messagesEndRef={messagesEndRef}
                  onConfirmReport={onConfirmReport}
                  onRate={onRate}
                  onShare={onShare}
                />
              </Box>
            </Box>
            <ChatInput {...chatInputProps} centered={false} />
          </>
        )}
      </Box>
    </>
  );
}
